/**
 * Level 8 check-ups. Beginning = the Level 7 End Check-Up (each level's post is the next level's pre).
 * End = the Level 8 Final Test in the book's shape (claude/level-8-source/_test.py) — 48 items, strand-scored:
 * Unit I 11, Unit II 13, Unit III 16, Data 5, Function notation 3 — with new numbers. Oct 30 standards audit: number
 * bases only; the three function-notation items became 8.F.1–2 items (domain FN kept); items added for the lessons added
 * since (3a, 5a, 17a, 19a, 21a, 22, 25a) — 55 items: Unit I 13, Unit II 14, Unit III 19, Data 6, Functions 3.
 */
import type { CheckItem, Item, Lesson } from "../types";
import { L7_POST } from "../level7";
import { converseItem, extItem, fitInterpItem, fnCmpItem, isFnItem, ratIrrItem, sciOpsItem, seqItem, sysCountItem } from "../skills/level8-extra";
import { distItem, lawItem, rootItem, sciItem, zeroNegItem } from "../skills/level8-l1to9";
import { dilateItem, linearItem, mbItem, ruleFromItem, steeperItem, systemItem, tableRuleItem, transformItem, twoPtItem } from "../skills/level8-l10to19";
import { distItem as gridDistItem, leanItem, pythItem, triAngleItem, vol8Item, wideNarrowItem } from "../skills/level8-l20to28";

const Q = (o: Omit<Item, "id" | "skill" | "hints"> & { skill?: string }): Item => ({ id: "", skill: "check-item", hints: [], ...o });
const checkItems = (tag: string, list: [Item, CheckItem["domain"], number][]): CheckItem[] =>
  list.map(([it, domain, lesson], k) => ({ ...it, id: `l8.${tag}:${k + 1}`, skill: "check-item", hints: [], diagnose: undefined, reveal: undefined, domain, lesson }));

const POST_L8 = checkItems("post", [
  // Unit I — Exponents, Roots, Equations (Lessons 1–8): 11
  [lawItem("×", "6", 7, 4), "U1", 1],
  [lawItem("÷", "5", 9, 3), "U1", 1],
  [lawItem("()", "2", 5, 3), "U1", 1],
  [zeroNegItem(9, 0), "U1", 2],
  [zeroNegItem(4, -2), "U1", 2],
  [sciItem(6.3, 4), "U1", 3],
  [rootItem(196, 2), "U1", 4],
  [rootItem(125, 3), "U1", 5],
  [Q({ prompt: "Solve: <b>6x + 5 = 47</b> &nbsp; x =", input: { kind: "number" }, answer: "7" }), "U1", 6],
  [Q({ prompt: "Solve: <b>7x − 2 = 3x + 18</b> &nbsp; x =", input: { kind: "number" }, answer: "5" }), "U1", 6],
  [distItem(-4, -2), "U1", 7],
  [sciOpsItem("÷", 9, 8, 3, 5), "U1", 31],
  [ratIrrItem(["√12", "irrational", "12 isn't a perfect square"]), "U1", 51],
  // Unit II — Lines and Functions (Lessons 9–17): 13
  [tableRuleItem(5, 2, 0, "move"), "U2", 10],
  [mbItem(6, 4), "U2", 13],
  [ruleFromItem(2, 9, "mb"), "U2", 13],
  [twoPtItem(1, 3, 4, 12), "U2", 14],
  [ruleFromItem(5, 2, "graph"), "U2", 14],
  [Q({ prompt: "<b>y = 6x</b>. Where does it cross the y axis?", input: { kind: "number" }, answer: "0", alsoAccept: ["(0,0)", "origin", "the origin"] }), "U2", 12],
  [steeperItem(4, 9), "U2", 12],
  [linearItem([2, 3, 6, 11, 18]), "U2", 15],
  [linearItem([3, 10, 17, 24, 31]), "U2", 15],
  [Q({ prompt: "A flat piece on a story graph means:", input: { kind: "choice", options: ["stopped", "running", "walking", "heading home"] }, answer: "stopped" }), "U2", 16],
  [systemItem("both", 4, 0, 0, 3), "U2", 17],
  [systemItem("one", 0, 4, 1, 5), "U2", 17],
  [sysCountItem(4, 1, 4, -3, false), "U2", 171],
  [Q({ prompt: "Which axis is b always on?", input: { kind: "choice", options: ["the y axis", "the x axis"] }, answer: "the y axis" }), "U2", 13],
  // Unit III — Geometry (Lessons 18–24): 16
  [transformItem("slide", 5, 2, -6, 0), "U3", 18],
  [transformItem("flip", 3, 5), "U3", 18],
  [transformItem("turn", 2, 6), "U3", 18],
  [dilateItem(5, 2, 3), "U3", 19],
  [dilateItem(16, 10, 1, 2), "U3", 19],
  [wideNarrowItem(125, true), "U3", 20],
  [wideNarrowItem(42, false), "U3", 20],
  [triAngleItem(55, 80), "U3", 21],
  [triAngleItem(90, 34), "U3", 21],
  [Q({ prompt: "A triangle's three corners are all equal. Each one is:", input: { kind: "number", label: "°" }, answer: "60" }), "U3", 21],
  [pythItem([7, 24, 25], "hyp"), "U3", 22],
  [pythItem([9, 12, 15], "leg"), "U3", 22],
  [gridDistItem(8, 15), "U3", 23],
  [vol8Item("box", 7, 2, 3), "U3", 24],
  [vol8Item("cyl", 4, 3), "U3", 24],
  [vol8Item("sphere", 6), "U3", 24],
  [seqItem([3, 1], { kind: "flipX" }, { kind: "slide", dx: -5, dy: 0 }), "U3", 191],
  [extItem(48, 67, "ext"), "U3", 211],
  [converseItem(9, 12, 15), "U3", 22],
  // Data (Lessons 25–26): 5
  [leanItem(["Hours practiced and songs learned", "climb"]), "DATA", 25],
  [Q({ prompt: "A dot far from the crowd is called:", input: { kind: "choice", options: ["an outlier", "the origin", "the mean", "the best-fit line"] }, answer: "an outlier" }), "DATA", 25],
  [Q({ prompt: "<b>15 out of 20</b> = ___%", input: { kind: "number", label: "%" }, answer: "75" }), "DATA", 26],
  [Q({ prompt: "In a two-way table, rows add across. Columns:", input: { kind: "choice", options: ["add down", "add across", "multiply"] }, answer: "add down" }), "DATA", 26],
  [Q({ prompt: "Must a line of best fit touch every dot?", input: { kind: "choice", options: ["no", "yes"] }, answer: "no" }), "DATA", 25],
  [fitInterpItem(["hours studied", "test score", "points per hour studied", "the score with no studying"], 4, 60, "predict", 5), "DATA", 251],
  // Functions (Lesson 27, 8.F.1–2 — it replaced function notation, Oct 30): 3
  [isFnItem([[0, 3], [1, 5], [1, 7], [2, 9]], "table"), "FN", 27],
  [isFnItem([[-2, 4], [-1, 1], [1, 1], [2, 4]], "pairs"), "FN", 27],
  [fnCmpItem({ m: 2, b: 7, form: "eq" }, { m: 5, b: 1, form: "table" }, "move"), "FN", 27],
]);

/** The Level 7 End Check-Up, offered again as Level 8's beginning. Same step id, so one sitting counts for both. */
export const L8_PRE: Lesson = { id: "L8.pre", level: "8", num: 0, title: "Beginning Check-Up", blurb: "the Level 7 End Check-Up", steps: [{ ...L7_POST.steps[0]!, intro: "This is the Level 7 End Check-Up — it shows whether Level 7 is solid before Level 8. If you've already taken it, your results are here." } as Lesson["steps"][number]] };
export const L8_POST: Lesson = { id: "L8.post", level: "8", num: 30, title: "Level 8 Final Check-Up", blurb: "the whole book, strand by strand", steps: [{ kind: "check", id: "L8post", title: "Level 8 Final Check-Up", intro: "The book's final test with new numbers: exponents, roots and equations; lines and functions; geometry; data; functions. Some answers won't come out tidy — a fraction or a root is a finished answer. Do what you can; leave what you can't.", items: POST_L8 }] };
